import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import Section from '../components/Section';
import MarketTicker from '../components/MarketTicker';
import ShareButtons from '../components/ShareButtons';
import EmailCaptureForm from '../components/EmailCaptureForm';
import Arrow from '../components/Arrow';
import { useLanguage } from '../context/LanguageContext';
import { fetchPost } from '../api';

function ReadingProgress() {
  const [pct, setPct] = useState(0);

  useEffect(() => {
    const onScroll = () => {
      const max = document.documentElement.scrollHeight - window.innerHeight;
      setPct(max > 0 ? Math.min(100, (window.scrollY / max) * 100) : 0);
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <div className="progress" aria-hidden>
      <div className="progress__bar" style={{ width: `${pct}%` }} />
    </div>
  );
}

export default function MarketPulsePost() {
  const { slug } = useParams();
  const { t, lang } = useLanguage();
  const [post, setPost] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    let active = true;
    setPost(null);
    setError('');
    fetchPost(slug, lang)
      .then((r) => active && setPost(r.post))
      .catch((e) => active && setError(e.message));
    return () => { active = false; };
  }, [slug, lang]);

  if (error) {
    return (
      <Section title="Not found">
        <p className="muted">{error}</p>
        <Link to="/market-pulse" className="btn btn--ghost mt-3">
          <Arrow left className="btn__arrow btn__arrow--back" /> {t('cta.viewAll')}
        </Link>
      </Section>
    );
  }
  if (!post) return <Section><p className="loading">Loading…</p></Section>;

  return (
    <Section>
      <ReadingProgress />
      <article className="article">
        <Link to="/market-pulse" className="btn btn--link">
          <Arrow left className="btn__arrow btn__arrow--back" /> {t('nav.pulse')}
        </Link>

        <div className="pulse-card__meta" style={{ marginTop: '1.5rem' }}>
          <span>
            {new Date(post.date).toLocaleDateString(undefined, {
              day: 'numeric', month: 'long', year: 'numeric',
            })}
          </span>
          <span>·</span>
          <span>{post.author}</span>
          <span>·</span>
          <span>{post.readingMinutes} min read</span>
        </div>

        <h1 className="article__title">{post.title}</h1>

        <div style={{ margin: '0 0 1.75rem', display: 'flex', gap: '0.4rem', flexWrap: 'wrap' }}>
          {(post.tags || []).map((tag) => <span className="tag" key={tag}>{tag}</span>)}
        </div>

        {post.market && <div style={{ marginBottom: '2rem' }}><MarketTicker market={post.market} /></div>}

        <div className="article__body">
          {post.body.map((para, i) => <p key={i}>{para}</p>)}
        </div>

        <hr className="divider" />
        <ShareButtons title={post.title} />

        {post.disclaimer && <p className="article__disclaimer">{post.disclaimer}</p>}

        <div className="callout callout--accent mt-4">
          <h3 style={{ marginBottom: '0.5rem' }}>
            <span className="callout__dot" aria-hidden />{t('news.title')}
          </h3>
          <p style={{ marginBottom: '1.25rem' }}>{t('news.lead')}</p>
          <div style={{ maxWidth: 440, position: 'relative' }}>
            <EmailCaptureForm mode="newsletter" source="post-newsletter" />
          </div>
        </div>
      </article>
    </Section>
  );
}
